import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NavBar from './NavBar';
import { getUserLeads } from '../services/userService';
import Icon from './Icon';

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLeads: 0,
    leadsByConvention: {},
    recentLeads: 0,
    leadsWithEmail: 0,
    leadsWithLinkedIn: 0,
  });

  useEffect(() => {
    loadLeads();
  }, [user]);

  const loadLeads = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const userLeads = await getUserLeads(user.uid);
      setLeads(userLeads);
      calculateStats(userLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatReminderDate = (reminderDate) => {
    if (!reminderDate) return null;
    const date = reminderDate?.toDate ? reminderDate.toDate() : new Date(reminderDate);
    const now = new Date();
    const daysDiff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return { text: 'Overdue', isOverdue: true, days: Math.abs(daysDiff) };
    } else if (daysDiff === 0) {
      return { text: 'Today', isToday: true, days: 0 };
    } else if (daysDiff === 1) {
      return { text: 'Tomorrow', days: 1 };
    } else if (daysDiff <= 7) {
      return { text: `In ${daysDiff} days`, days: daysDiff };
    } else {
      return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), days: daysDiff };
    }
  };

  const calculateStats = (leadsData) => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const statsData = {
      totalLeads: leadsData.length,
      leadsByConvention: {},
      recentLeads: 0,
      leadsWithEmail: 0,
      leadsWithLinkedIn: 0,
      remindersCount: 0,
    };

    leadsData.forEach(lead => {
      // Count by convention
      const convention = lead.convention || 'Unknown';
      statsData.leadsByConvention[convention] = (statsData.leadsByConvention[convention] || 0) + 1;

      // Count recent leads (last 7 days)
      if (lead.createdAt) {
        const createdAt = lead.createdAt.toDate ? lead.createdAt.toDate() : new Date(lead.createdAt);
        if (createdAt >= sevenDaysAgo) {
          statsData.recentLeads++;
        }
      }

      // Count leads with email
      if (lead.email) {
        statsData.leadsWithEmail++;
      }

      // Count leads with LinkedIn
      if (lead.linkedin_url) {
        statsData.leadsWithLinkedIn++;
      }

      // Count leads with reminders
      if (lead.reminderDate) {
        statsData.remindersCount++;
      }
    });

    setStats(statsData);
  };

  // Get reminders sorted by date (upcoming first, then overdue)
  const getUpcomingReminders = () => {
    const reminders = leads
      .filter(lead => lead.reminderDate)
      .map(lead => {
        const reminderDate = lead.reminderDate?.toDate ? lead.reminderDate.toDate() : new Date(lead.reminderDate);
        return {
          ...lead,
          reminderDateObj: reminderDate,
          reminderInfo: formatReminderDate(lead.reminderDate),
        };
      })
      .sort((a, b) => {
        // Sort: overdue first (negative days), then upcoming (positive days)
        const aDays = a.reminderInfo?.days || 0;
        const bDays = b.reminderInfo?.days || 0;
        return aDays - bDays;
      });

    return reminders;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center transition-colors duration-300">
        <div className="text-zinc-600 dark:text-zinc-400">Loading dashboard...</div>
      </div>
    );
  }

  const topConventions = Object.entries(stats.leadsByConvention)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      <NavBar title="Dashboard" />

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-1">
              {stats.totalLeads}
            </div>
            <div className="text-sm text-zinc-600 dark:text-zinc-400">Total Leads</div>
          </div>
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-1">
              {stats.recentLeads}
            </div>
            <div className="text-sm text-zinc-600 dark:text-zinc-400">Last 7 Days</div>
          </div>
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-1">
              {stats.leadsWithEmail}
            </div>
            <div className="text-sm text-zinc-600 dark:text-zinc-400">With Email</div>
          </div>
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-1">
              {stats.remindersCount}
            </div>
            <div className="text-sm text-zinc-600 dark:text-zinc-400">Reminders</div>
          </div>
        </div>

        {/* Upcoming Reminders */}
        {getUpcomingReminders().length > 0 && (
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <Icon emoji="⏰" size="text-lg" /> Upcoming Reminders
              </h2>
              <button
                onClick={() => navigate('/leads')}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {getUpcomingReminders().slice(0, 5).map((lead) => {
                const reminderInfo = formatReminderDate(lead.reminderDate);
                return (
                  <div
                    key={lead.id}
                    className={`p-4 rounded-lg border ${
                      reminderInfo?.isOverdue
                        ? 'bg-red-900/20 border-red-600/50'
                        : reminderInfo?.isToday
                        ? 'bg-yellow-900/20 border-yellow-600/50'
                        : 'bg-blue-900/20 border-blue-600/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon emoji="⏰" size="text-lg" />
                          <span className={`text-sm font-medium ${
                            reminderInfo?.isOverdue
                              ? 'text-red-300'
                              : reminderInfo?.isToday
                              ? 'text-yellow-300'
                              : 'text-blue-300'
                          }`}>
                            {reminderInfo?.text}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-1">
                          {lead.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                          {lead.company || 'No company'} {lead.title ? `• ${lead.title}` : ''}
                        </div>
                        {lead.reminderNote && (
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-2 italic">"{lead.reminderNote}"</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {getUpcomingReminders().length > 5 && (
              <button
                onClick={() => navigate('/leads')}
                className="w-full mt-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
              >
                View {getUpcomingReminders().length - 5} more reminder{getUpcomingReminders().length - 5 !== 1 ? 's' : ''} →
              </button>
            )}
          </div>
        )}

        {/* Leads by Convention */}
        {topConventions.length > 0 && (
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 flex items-center gap-2">
              <Icon emoji="📊" size="text-lg" /> Leads by Convention
            </h2>
            <div className="space-y-3">
              {topConventions.map(([convention, count]) => {
                const percentage = stats.totalLeads > 0 
                  ? Math.round((count / stats.totalLeads) * 100) 
                  : 0;
                return (
                  <div key={convention} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-700 dark:text-zinc-300 font-medium">{convention}</span>
                      <span className="text-zinc-600 dark:text-zinc-400">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 flex items-center gap-2">
            <Icon emoji="⚡" size="text-lg" /> Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/')}
              className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Icon emoji="📷" size="text-base" className="mr-1" /> Scan Card
            </button>
            <button
              onClick={() => navigate('/leads')}
              className="py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg font-medium transition-colors flex items-center justify-center gap-1"
            >
              <Icon emoji="📋" size="text-base" /> View Leads
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        {leads.length > 0 && (
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 flex items-center gap-2">
              <Icon emoji="🕒" size="text-lg" /> Recent Activity
            </h2>
            <div className="space-y-3">
              {leads
                .sort((a, b) => {
                  const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                  const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                  return dateB - dateA;
                })
                .slice(0, 5)
                .map((lead) => {
                  const date = lead.createdAt?.toDate 
                    ? lead.createdAt.toDate() 
                    : new Date(lead.createdAt || Date.now());
                  const timeAgo = getTimeAgo(date);
                  
                  return (
                    <div key={lead.id} className="flex items-center justify-between p-3 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {lead.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-zinc-600 dark:text-zinc-400">
                          {lead.company || 'No company'} • {lead.convention || 'No event'}
                        </div>
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-500 ml-2">
                        {timeAgo}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {leads.length === 0 && (
          <div className="text-center py-12">
            <div className="mb-4"><Icon emoji="📊" size="text-6xl" /></div>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg mb-2">No leads yet</p>
            <p className="text-zinc-500 dark:text-zinc-500 text-sm mb-4">Start scanning business cards to see statistics</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Scan Your First Card
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function getTimeAgo(date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return date.toLocaleDateString();
}

export default Dashboard;

